import { ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  PlanAccountRef, PlanBody, PlansStripeMeta, PriceMigration, createPlanApi, getPlanApi,
  listPlansApi, plansStripeMeta, clearPlanStripeIds, pushPlan, retirePlan, savePlanApi,
} from '../../api/platform';
import { FEATURE_KEYS, LIMIT_KEYS, Plan, PlanFeatures, PlanLimits, atLeastPlatform } from '../../shared/types';
import {
  INFINITY, LIMIT_LABEL, bytesFromGb, formatStorage, gbFromBytes, money, moneyExact, planDraft,
} from './planDisplay';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { ActionBar, RowCard, Toggle } from '../../components/fs';
import { usePlatform } from './PlatformLayout';
import { DRAFT_TITLE, neverPushed } from './PlansAdmin';

/** A plan being edited, before it is a plan. */
interface Draft {
  key: string;
  name: string;
  tagline: string;
  public: boolean;
  popular: boolean;
  trialable: boolean;
  monthlyDollars: string;
  annualDiscountPct: string;
  currency: string;
  limits: PlanLimits;
  /**
   * Every feature the server knows about, read off FEATURE_KEYS rather than
   * named one by one. The save posts this object whole and the server reads
   * an absent key as false, so a feature missing from the draft is a feature
   * this editor silently switches OFF for every plan it touches.
   */
  features: PlanFeatures;
}

/** What each feature's switch says, and what it unlocks. */
const FEATURE_LABEL: Record<keyof PlanFeatures, [name: string, detail: string]> = {
  sso: ['SSO access', 'unlocks the Account access page'],
  api: ['API access', 'API keys and the public REST API'],
  customBranding: ['Custom branding', 'logo, fonts, presets and dark mode on forms'],
  teamAccess: ['Team access', 'folders, the role ladder and grants, and named groups'],
  hideAdvertisement: ['Hide the Form-Magic badge', 'lets a form turn off the “Powered by Form-Magic.com” link'],
  smartPdf: ['Smart PDF — upload your own PDF template', 'the one thing on the Document tab that is not free; arranging the parts and the branded record are on every plan'],
  webhooks: ['Account webhooks', 'one endpoint for the whole account, told when a submission is received, edited, paid for or deleted — the form-level webhook step is on every plan and is not this'],
  aiBuilder: ['AI form building and assistant', 'describe a form, get a draft; ask a form about its submissions and have it propose edits — spends the platform’s own provider tokens, so it is never on Free; each has a monthly ceiling among the limits'],
};

/** Every feature off — the shape a new plan starts from. */
const NO_FEATURES = Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])) as unknown as PlanFeatures;

/** A plan row's features, with any key the row predates read as false. */
const featuresOf = (plan: Plan): PlanFeatures =>
  Object.fromEntries(
    FEATURE_KEYS.map((k) => [k, plan.features?.[k] === true])
  ) as unknown as PlanFeatures;

const EMPTY_LIMITS: PlanLimits = {
  forms: 5, submissionsPerMonth: 100, users: 1, storageBytes: 100000000,
  submissionRecords: 500, fieldsPerForm: 100, aiGenerationsPerMonth: 25, aiAssistantMessagesPerMonth: 200,
};

const draftOf = (plan: Plan): Draft => ({
  key: plan.key,
  name: plan.name,
  tagline: plan.tagline,
  public: plan.public,
  popular: plan.popular,
  trialable: plan.trialable,
  monthlyDollars: plan.price_monthly_cents === null ? '' : String(plan.price_monthly_cents / 100),
  annualDiscountPct: String(plan.annual_discount_pct),
  currency: plan.currency.toUpperCase(),
  limits: { ...plan.limits },
  features: featuresOf(plan),
});

/**
 * A new plan starts blank except for the Smart PDF and the AI builder,
 * which start ON — and it starts hidden.
 *
 * Free already exists; anything being created here is a paid plan, and both
 * are on every paid plan by decision (053, 065). Typing `free` as the key
 * turns them back off — see the Key field — so the one row they must not be
 * on cannot get them by forgetting.
 *
 * `public: false` (R12, D6): "+ New plan" makes a Draft — not on the picker
 * and not in Stripe — until somebody switches Public on and pushes it.
 */
const NEW_DRAFT: Draft = {
  key: '', name: '', tagline: '', public: false, popular: false, trialable: true,
  monthlyDollars: '', annualDiscountPct: '20', currency: 'USD', limits: { ...EMPTY_LIMITS },
  features: { ...NO_FEATURES, smartPdf: true, aiBuilder: true },
};

/** Dollars in the field, cents on the wire — never a float of a currency. */
function centsOf(dollars: string): number | null | undefined {
  const t = dollars.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  return Math.round(Number(t) * 100);
}

/**
 * What the push route answers beyond the plan (`plans.ts`: `{ success,
 * ...PushReport }`). The shared `pushPlan` type names only `plan`; the page
 * reads the rest to say what moved, and survives a server that omits it.
 */
type PushAnswer = Awaited<ReturnType<typeof pushPlan>> & {
  migration?: PriceMigration;
  /** subscriptions moved, or scheduled to move, to the new price */
  migrated?: number;
  /** one entry per interval whose price actually moved */
  changes?: unknown[];
};

const accountsWord = (n: number) => `${n} account${n === 1 ? '' : 's'}`;

/** The push toast: what reached Stripe, and who it moved. */
export function pushToast(used: PriceMigration, answer: PushAnswer): string {
  if (Array.isArray(answer.changes) && answer.changes.length === 0) {
    return 'Pushed to Stripe. Its prices already matched — no new Price, nobody moved.';
  }
  const n = answer.migrated;
  if (typeof n !== 'number') return 'Pushed to Stripe. The new price ids are in the rail.';
  if (used === 'at_renewal') {
    return `Pushed to Stripe. ${accountsWord(n)} ${n === 1 ? 'moves at its' : 'move at their'} next renewal.`;
  }
  if (used === 'now_prorated') return `Pushed to Stripe. ${accountsWord(n)} moved now, prorated.`;
  return 'Pushed to Stripe. Existing accounts keep their price.';
}

/**
 * One plan, created or edited on the same page — there is no wizard
 * (R12, screen 41, D6).
 *
 * Three things here are deliberate. Limits take effect on save, which is why
 * they sit beside a plain Save button; the annual price is never typed — it
 * is the monthly rate less a percentage, and the derived amount is shown next
 * to the percentage; and prices only reach Stripe on push, which mints a new
 * Price and archives the old rather than editing it, so a saved price change
 * sits here as a loudly-marked draft until someone means it.
 *
 * The form is four row-card sections — Plan & price, Visibility, Limits,
 * Features — still ONE form: Save (or Enter in any field) posts all of it.
 * The Stripe and accounts rails sit beside it from 1100px and under it below;
 * the action bar sticks to the bottom of the column.
 */
export default function PlanEditor() {
  const { role } = usePlatform();
  const { id } = useParams();
  const navigate = useNavigate();
  // `/platform/plans/new` is its own route and carries no :id, so a new plan is
  // the absence of one. Comparing against the string 'new' looked right and was
  // never true, which sent Add Plan off to fetch plan "NaN".
  const isNew = !id;
  const canEdit = atLeastPlatform(role, 'admin');

  const [draft, setDraft] = useState<Draft | null>(isNew ? { ...NEW_DRAFT } : null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [accounts, setAccounts] = useState<PlanAccountRef[]>([]);
  const [meta, setMeta] = useState<PlansStripeMeta>({ stripeMode: null, webhookUrl: '', webhookHealthy: null });
  const [migration, setMigration] = useState<PriceMigration>('grandfather');
  const [pushing, setPushing] = useState(false);
  const [confirmId, setConfirmId] = useState<'push' | 'retire' | 'clear' | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();

  /**
   * A plan is a page of numbers with real consequences — limits take effect on
   * every account the moment they are stored — so leaving with one retyped and
   * unsaved is worth a question. New plans have nothing stored yet, so their
   * baseline is the blank form.
   */
  const { dirty, markSaved } = useDirty(draft, !!draft);
  const guard = useUnsavedGuard({
    dirty,
    what: isNew ? 'This plan has not been created yet, and what you typed' : `Your changes to ${plan?.name || 'this plan'}`,
  });

  useEffect(() => {
    if (!isNew) {
      getPlanApi(Number(id))
        .then((r) => { setPlan(r.plan); setDraft(draftOf(r.plan)); setAccounts(r.accounts); })
        .catch((e) => setError(e.message));
    }
    // The list response carries the Stripe connection state the rail renders.
    listPlansApi()
      .then((r) => setMeta(plansStripeMeta(r.stripe)))
      .catch(() => undefined);
  }, [id, isNew]);

  if (!draft) {
    return (
      <div className="pl-page pl-editor">
        <Link className="pl-back" to="/platform/plans">← Plans</Link>
        <div className="fs-card pad">{error ? <span className="ad-err">{error}</span> : 'Loading…'}</div>
      </div>
    );
  }

  // Every setter un-arms a pending confirm: a "Confirm…" must confirm what
  // is on the screen, not what was there when it was armed.
  const set = <K extends keyof Draft>(key: K, v: Draft[K]) => {
    setConfirmId(null);
    setDraft({ ...draft, [key]: v });
  };
  // A changed answer to "who moves?" un-arms any pending confirm.
  const pickMigration = (m: PriceMigration) => { setConfirmId(null); setMigration(m); };
  const setLimit = (key: keyof PlanLimits, v: number | null) => {
    setConfirmId(null);
    setDraft({ ...draft, limits: { ...draft.limits, [key]: v } });
  };
  const setFeature = (key: keyof PlanFeatures, on: boolean) => {
    setConfirmId(null);
    setDraft({ ...draft, features: { ...draft.features, [key]: on } });
  };

  const monthlyCents = centsOf(draft.monthlyDollars);
  const discount = Number(draft.annualDiscountPct);
  const discountOk = Number.isInteger(discount) && discount >= 0 && discount <= 100;
  // The same rule the server applies on save, shown live so the percentage is
  // never chosen without the amount it produces being visible.
  const annualCents = monthlyCents === null || monthlyCents === undefined || !discountOk
    ? null : Math.round(monthlyCents * 12 * (1 - discount / 100));
  const savingCents = annualCents === null || monthlyCents === null || monthlyCents === undefined
    ? null : monthlyCents * 12 - annualCents;

  const connected = meta.stripeMode !== null;
  const pushedMonthly = plan?.pushed_monthly_cents ?? null;
  const pushedAnnual = plan?.pushed_annual_cents ?? null;
  // Amber marks the field that differs from what Stripe holds — computed off
  // the field itself, so it appears the moment someone types a new number.
  const monthlyDiffers = pushedMonthly !== null && monthlyCents !== undefined && monthlyCents !== pushedMonthly;
  const annualDiffers = pushedAnnual !== null && annualCents !== null && annualCents !== pushedAnnual;
  const priceDiffers = monthlyDiffers || annualDiffers;
  const savedDraft = plan ? planDraft(plan) : null;
  const heading = isNew ? 'New plan' : draft.name || plan?.name;

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (monthlyCents === undefined) { setError('Monthly price must be an amount like 50 or 49.99'); return; }
    if (!discountOk) { setError('The annual discount must be a whole percentage from 0 to 100'); return; }

    const body: PlanBody = {
      name: draft!.name.trim(),
      tagline: draft!.tagline.trim(),
      public: draft!.public,
      popular: draft!.popular,
      trialable: draft!.trialable,
      priceMonthlyCents: monthlyCents,
      annualDiscountPct: discount,
      currency: draft!.currency.toLowerCase(),
      limits: draft!.limits,
      features: draft!.features,
    };
    try {
      if (isNew) {
        const r = await createPlanApi({ ...body, key: draft!.key.trim().toLowerCase() });
        markSaved();
        toast(`${r.plan.name} created.`);
        navigate(`/platform/plans/${r.plan.id}`, { replace: true });
      } else {
        const r = await savePlanApi(Number(id), body);
        setPlan(r.plan);
        setDraft(draftOf(r.plan));
        markSaved();
        toast('Saved. Limits take effect immediately.');
      }
    } catch (err: any) { setError(err.message); }
  }

  /**
   * The only call that writes to Stripe. Never navigates; a failed push
   * leaves the draft intact so the number can be checked rather than retyped.
   * Moving live subscribers with a prorated charge is the one migration that
   * bills people today, so it alone asks twice — and says so while it asks.
   * The toast names who moved (the route's `migrated`), not what was typed.
   */
  async function onPush() {
    if (migration === 'now_prorated' && confirmId !== 'push') { setConfirmId('push'); return; }
    setConfirmId(null);
    setError('');
    setPushing(true);
    try {
      const r = (await pushPlan(Number(id), migration)) as PushAnswer;
      setPlan(r.plan);
      setDraft(draftOf(r.plan));
      markSaved();
      toast(pushToast(r.migration ?? migration, r));
    } catch (err: any) { setError(err.message); }
    setPushing(false);
  }

  /**
   * Forget the stored ids so the next push creates the Product afresh.
   *
   * Only correct when Stripe genuinely no longer has the Product; on a plan
   * still pointing at a live one this would unlink it. Hence the second ask,
   * and the server's refusal while anyone is subscribed.
   */
  async function onClearStripeIds() {
    if (confirmId !== 'clear') { setConfirmId('clear'); return; }
    setConfirmId(null);
    setError('');
    try {
      const r = await clearPlanStripeIds(Number(id));
      setPlan(r.plan);
      setDraft(draftOf(r.plan));
      markSaved();
      toast('Stripe ids cleared. Push to create the Product again.', { kind: 'info' });
    } catch (err: any) { setError(err.message); }
  }

  /** Retire asks twice; bringing back is one click. Both now say what happened. */
  async function onRetire(retired: boolean) {
    if (retired && confirmId !== 'retire') { setConfirmId('retire'); return; }
    setConfirmId(null);
    setError('');
    try {
      const r = await retirePlan(Number(id), retired);
      setPlan(r.plan);
      const name = r.plan?.name || plan?.name || 'The plan';
      toast(retired
        ? `${name} retired. It no longer appears on the plan picker or the pricing page.`
        : `${name} brought back.${r.plan?.public ? ' It is on the plan picker again.' : ' It stays hidden until Public is on.'}`);
    } catch (err: any) {
      // A 409 names the subscribers still on the plan — show them as-is.
      setError(err.message);
    }
  }

  // The note in the action bar: what a pending confirm will do, else what
  // saving will do, else the standing rule of the two buttons.
  const note: { text: ReactNode; warn?: boolean } = confirmId === 'push'
    ? {
      warn: true,
      text: 'This moves every subscriber on the old price to the new one now, and Stripe charges each of them a prorated amount today. Press Confirm… to push.',
    }
    : confirmId === 'retire'
      ? {
        warn: true,
        text: `Retiring hides ${plan?.name || 'this plan'} from the plan picker and the pricing page. It is refused while anyone is still on it. Press Confirm… to retire it.`,
      }
      : dirty && !isNew
        ? { text: `Unsaved changes. Limits and features apply to the ${accountsWord(accounts.length)} on this plan as soon as you save; prices only reach Stripe on push, which sends the saved plan.` }
        : {
          text: 'Limits take effect on save; prices only reach Stripe on push. Retiring hides a plan from the picker and is refused while anyone is still on it.',
        };

  const metaLine = isNew
    ? `A new plan — ${draft.public ? 'on the plan picker once it is created' : 'hidden from the plan picker until Public is on'}, and not in Stripe until its first push.`
    : [`${accountsWord(accounts.length)} on this plan`, draft.tagline.trim() || plan?.tagline].filter(Boolean).join(' · ');

  return (
    <form onSubmit={onSave} className="pl-page pl-editor">
      <Link className="pl-back" to="/platform/plans">← Plans</Link>
      <div className="fs-page-head pl-head">
        <div className="text">
          <div className="pl-ed-titlerow">
            {/* Follows the name field as it is typed. */}
            <h1>{heading}</h1>
            {plan?.stripe_product_id ? (
              <span className="fs-pill ok">live in Stripe</span>
            ) : (
              <span className="fs-pill neutral" title={connected ? 'Pushed to Stripe on first push' : 'No Stripe secret key — set one in Platform settings, or as STRIPE_SECRET_KEY'}>
                not in Stripe
              </span>
            )}
            {plan && neverPushed(plan) && <span className="fs-pill neutral" title={DRAFT_TITLE}>Draft</span>}
            {savedDraft && <span className="fs-pill warn">unpushed price change</span>}
            {plan?.retired_at && <span className="fs-pill danger">retired</span>}
          </div>
          <div className="fs-page-sub">{metaLine}</div>
        </div>
      </div>

      {error && <div className="ad-err">{error}</div>}

      <div className="pl-cols pl-ed-cols">
        <div className="fs-main-col pl-ed-main">
          <section className="fs-card st-card" aria-labelledby="pl-ed-plan">
            <div className="st-card-head"><h2 id="pl-ed-plan">Plan &amp; price</h2></div>
            <RowCard label={<label htmlFor="pl-ed-name">Name</label>} hint="Shown to customers">
              <input id="pl-ed-name" className="fs-input pl-ed-wide" value={draft.name} required disabled={!canEdit}
                onChange={(e) => set('name', e.target.value)} />
            </RowCard>
            <RowCard
              label={<label htmlFor="pl-ed-key">Key</label>}
              hint={isNew ? 'Lowercase letters, digits and underscores. Fixed once the plan exists.' : 'Fixed after creation'}
            >
              {isNew ? (
                <input id="pl-ed-key" className="fs-input pl-ed-mono pl-ed-mid" value={draft.key} required
                  placeholder="enterprise_limited" disabled={!canEdit}
                  onChange={(e) => {
                    const key = e.target.value;
                    // The Smart PDF and the AI builder follow the key while
                    // the plan is being created: on for every paid plan, off
                    // for Free. Still switches — pricing can overrule them
                    // below and that choice survives, because this only
                    // fires on typing.
                    const paid = key.trim().toLowerCase() !== 'free';
                    setConfirmId(null);
                    setDraft({
                      ...draft,
                      key,
                      features: { ...draft.features, smartPdf: paid, aiBuilder: paid },
                    });
                  }} />
              ) : (
                /* Fixed after creation: it identifies the plan in Stripe
                   metadata and on every invoice already issued. */
                <input id="pl-ed-key" className="fs-input pl-ed-mono pl-ed-mid" value={draft.key} disabled />
              )}
            </RowCard>
            <RowCard label={<label htmlFor="pl-ed-tagline">Tagline</label>} hint="One line on the plan card">
              <input id="pl-ed-tagline" className="fs-input pl-ed-wide" value={draft.tagline} disabled={!canEdit}
                placeholder="A working group with real volume."
                onChange={(e) => set('tagline', e.target.value)} />
            </RowCard>
            <RowCard label={<label htmlFor="pl-ed-monthly">Monthly ({draft.currency})</label>}>
              <input id="pl-ed-monthly" value={draft.monthlyDollars} disabled={!canEdit} inputMode="decimal" placeholder="0"
                className={`fs-input pl-ed-num${monthlyDiffers ? ' pl-differs' : ''}`}
                onChange={(e) => set('monthlyDollars', e.target.value)} />
              {monthlyDiffers ? (
                <div className="pl-was">was {moneyExact(pushedMonthly, draft.currency)} · not pushed</div>
              ) : (
                <div className="pl-ed-hint">Leave empty for a plan that is not a purchase.</div>
              )}
            </RowCard>
            <RowCard
              label={<label htmlFor="pl-ed-discount">Annual discount</label>}
              hint="Annual is the monthly rate less a discount, charged once at the start of the year. The saving shown on the picker is computed, never typed."
            >
              <div className="pl-ed-inline">
                <input id="pl-ed-discount" value={draft.annualDiscountPct} disabled={!canEdit} inputMode="numeric"
                  className={`fs-input pl-ed-pct${!monthlyDiffers && annualDiffers ? ' pl-differs' : ''}`}
                  onChange={(e) => set('annualDiscountPct', e.target.value)} />
                <span className="st-muted">% off, billed up front</span>
              </div>
              <div className="pl-ed-hint pl-tabular">
                {annualCents === null
                  ? 'No annual price without a monthly one.'
                  : `${money(annualCents, draft.currency)} once a year · saves ${money(savingCents, draft.currency)}`}
              </div>
            </RowCard>
            <RowCard label={<label htmlFor="pl-ed-currency">Currency</label>}>
              <select id="pl-ed-currency" className="fs-input pl-ed-num" value={draft.currency} disabled={!canEdit}
                onChange={(e) => set('currency', e.target.value)}>
                {['USD', 'CAD', 'EUR'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </RowCard>
            {connected && priceDiffers && plan?.stripe_price_monthly ? (
              <div className="pl-ed-body">
                <div className="pl-push-warn">
                  <b>
                    Pushing this creates a new Stripe Price and archives{' '}
                    <code className="pl-ed-code">{plan.stripe_price_monthly}</code>.
                  </b>{' '}
                  Nobody's bill changes until you say so
                  {accounts.length > 0 ? (
                    <>
                      {' '}— decide what happens to the {accountsWord(accounts.length)} already
                      on the old price:
                      <div className="pl-ed-migrations">
                        <MigrationCard value="grandfather" current={migration} onPick={pickMigration} disabled={!canEdit}
                          title="Leave them on the old price"
                          detail={`Grandfathered indefinitely. New signups pay ${money(monthlyCents ?? null, draft.currency)}.`} />
                        <MigrationCard value="at_renewal" current={migration} onPick={pickMigration} disabled={!canEdit}
                          title="Move them at their next renewal"
                          detail="No proration. We email each account 14 days before their renewal date." />
                        <MigrationCard value="now_prorated" current={migration} onPick={pickMigration} disabled={!canEdit}
                          title="Move them now, prorated"
                          detail="Stripe issues a prorated charge immediately. Use only for a price cut." />
                      </div>
                    </>
                  ) : (
                    <> — and nobody is on the old price, so pushing moves no subscriber.</>
                  )}
                </div>
              </div>
            ) : !connected ? (
              <div className="pl-ed-body">
                <div className="pl-warn">
                  <b>Prices are not pushed anywhere yet.</b> Stripe is not connected on this server, so a price
                  here is the number the catalogue says — nothing is charged and no subscriber's bill moves.
                  When Stripe lands, pushing will create a new Price and archive the old one rather than editing it.
                </div>
              </div>
            ) : null}
          </section>

          <section className="fs-card st-card" aria-labelledby="pl-ed-visibility">
            <div className="st-card-head"><h2 id="pl-ed-visibility">Visibility</h2></div>
            <RowCard
              label={<label htmlFor="pl-ed-public">Public</label>}
              actions={<Toggle id="pl-ed-public" checked={draft.public} disabled={!canEdit} onChange={(v) => set('public', v)} />}
            >
              <span className={draft.public ? undefined : 'st-muted'}>
                {draft.public
                  ? 'Visible on the plan picker and the public pricing page.'
                  : 'Hidden from the plan picker and the pricing page. Accounts already on it keep it.'}
              </span>
            </RowCard>
            <RowCard
              label={<label htmlFor="pl-ed-popular">Most popular</label>}
              actions={<Toggle id="pl-ed-popular" checked={draft.popular} disabled={!canEdit} onChange={(v) => set('popular', v)} />}
            >
              <span className={draft.popular ? undefined : 'st-muted'}>
                {draft.popular ? 'Marked as most popular on its card.' : 'No “Most popular” mark.'}
              </span>
            </RowCard>
            <RowCard
              label={<label htmlFor="pl-ed-trialable">Free upgrade periods</label>}
              actions={<Toggle id="pl-ed-trialable" checked={draft.trialable} disabled={!canEdit} onChange={(v) => set('trialable', v)} />}
            >
              <span className={draft.trialable ? undefined : 'st-muted'}>
                {draft.trialable
                  ? 'Available for free upgrade periods.'
                  : 'Not offered as a free upgrade period.'}
              </span>
            </RowCard>
          </section>

          <section className="fs-card st-card" aria-labelledby="pl-ed-limits">
            <div className="st-card-head">
              <div className="pl-headtext">
                <h2 id="pl-ed-limits">Limits</h2>
                <div className="hint">
                  Tick <b>{INFINITY}</b> to store <code>null</code> and skip the check — that is what an
                  unlimited plan is, not a very large number.
                </div>
              </div>
            </div>
            {LIMIT_KEYS.map((key) => (
              <LimitField key={key} limitKey={key} value={draft.limits[key]} disabled={!canEdit}
                onChange={(v) => setLimit(key, v)} />
            ))}
            <div className="pl-ed-body">
              <div className="pl-note">
                Lowering a limit below what a subscribed account already uses is allowed. Nothing is deleted
                and nothing breaks — the account keeps what it has and cannot add more.
                <b> Storage is the exception:</b> an account above a lowered storage ceiling keeps its files
                but will not be able to upload, and will not be able to move down a plan until it is under.
              </div>
            </div>
          </section>

          {/* Rendered off FEATURE_KEYS, so a feature added to the type
              appears here rather than being quietly posted as false. */}
          <section className="fs-card st-card" aria-labelledby="pl-ed-features">
            <div className="st-card-head">
              <div className="pl-headtext">
                <h2 id="pl-ed-features">Features</h2>
                <div className="hint">Like the limits, these apply to every account on the plan as soon as you save.</div>
              </div>
            </div>
            {FEATURE_KEYS.map((key) => (
              <RowCard
                key={key}
                label={<label htmlFor={`pl-ed-feature-${key}`}>{FEATURE_LABEL[key][0]}</label>}
                actions={(
                  <Toggle id={`pl-ed-feature-${key}`} checked={draft.features[key]} disabled={!canEdit}
                    onChange={(v) => setFeature(key, v)} />
                )}
              >
                <span className="st-muted">{FEATURE_LABEL[key][1]}</span>
              </RowCard>
            ))}
          </section>

          {canEdit && (
            <ActionBar className="pl-ed-bar">
              <span className={`pl-ed-note${note.warn ? ' warn' : ''}`} role={note.warn ? 'status' : undefined}>
                {note.text}
              </span>
              <span className="pl-ed-actions">
                <button className="fs-btn primary" type="submit">{isNew ? 'Create plan' : 'Save plan'}</button>
                {!isNew && connected && monthlyCents !== null && monthlyCents !== undefined && (
                  <button className="fs-btn neutral" type="button" disabled={pushing} onClick={onPush}>
                    {pushing ? 'Pushing…' : confirmId === 'push' ? 'Confirm…' : 'Push price to Stripe'}
                  </button>
                )}
                {!isNew && (
                  <button className="fs-btn danger" type="button" onClick={() => onRetire(!plan?.retired_at)}>
                    {plan?.retired_at ? 'Bring back' : confirmId === 'retire' ? 'Confirm…' : 'Retire plan'}
                  </button>
                )}
              </span>
            </ActionBar>
          )}
        </div>

        <div className="pl-rail pl-ed-rail">
          <section className="fs-card st-card" aria-labelledby="pl-ed-stripe">
            <div className="st-card-head">
              <h2 id="pl-ed-stripe">Stripe</h2>
              {connected
                ? <span className={`fs-pill ${meta.stripeMode === 'live' ? 'ok' : 'warn'}`}>{meta.stripeMode} mode</span>
                : <span className="fs-pill neutral">not connected</span>}
            </div>
            {connected ? (
              <div className="pl-rail-body">
                <div className="pl-mono">
                  <div><span>product</span>{railValue(plan?.stripe_product_id, pushing)}</div>
                  <div><span>price · monthly · live</span>{railValue(plan?.stripe_price_monthly, pushing)}</div>
                  <div><span>price · annual · live</span>{railValue(plan?.stripe_price_annual, pushing)}</div>
                  {savedDraft && (
                    <div>
                      <span>price · monthly · draft</span>
                      {pushing ? <b><span className="pl-spin" /></b>
                        : <b className="pl-warn-text">not created yet</b>}
                    </div>
                  )}
                </div>
                <div className="pl-rail-checks">
                  <RailCheck status={plan?.stripe_product_id ? 'pass' : 'pending'} title="Product exists"
                    detail={plan?.stripe_product_id || 'created on first push'} />
                  <RailCheck status={plan?.stripe_product_id ? 'pass' : 'pending'} title="Metadata written"
                    detail={plan?.stripe_product_id ? `plan_key, all ${LIMIT_KEYS.length} limits` : 'written at push'} />
                  <RailCheck
                    status={!plan?.stripe_product_id ? 'pending' : priceDiffers ? 'warn' : 'pass'}
                    title="Price differs from Stripe"
                    detail={!plan?.stripe_product_id
                      ? 'no price pushed yet'
                      : priceDiffers
                        ? `local ${money(monthlyDiffers ? monthlyCents ?? null : annualCents, draft.currency)} · Stripe ${money(monthlyDiffers ? pushedMonthly : pushedAnnual, draft.currency)} — push to reconcile`
                        : 'in sync'} />
                  <RailCheck
                    status={meta.webhookHealthy === null ? 'pending' : meta.webhookHealthy ? 'pass' : 'fail'}
                    title="Webhook healthy"
                    detail={meta.webhookHealthy === null ? 'no events yet' : meta.webhookHealthy ? 'receiving events' : 'no recent events'} />
                </div>
                {plan?.stripe_product_id && (
                  <div className="pl-rail-links">
                    <a className="pl-ed-link"
                      href={`https://dashboard.stripe.com/${meta.stripeMode === 'test' ? 'test/' : ''}products/${plan.stripe_product_id}`}
                      target="_blank" rel="noreferrer">
                      Open this product in Stripe ↗
                    </a>
                    {/* The way back when that link 404s: the Product was
                        deleted in Stripe, so the ids here point at nothing and
                        a push cannot tell that from a mode mix-up. Admin only,
                        as the route is. */}
                    {canEdit && (
                      <button type="button" onClick={onClearStripeIds}
                        className={`fs-btn text sm pl-clear-ids${confirmId === 'clear' ? ' danger' : ''}`}>
                        {confirmId === 'clear'
                          ? 'Confirm — forget these ids?'
                          : 'Product deleted in Stripe? Clear these ids'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="pl-rail-body">
                <div className="pl-rail-copy">
                  No <code>STRIPE_SECRET_KEY</code> on this server, so this plan has no Product and no Price.
                  The catalogue works without one — the limits are ours, the money is Stripe's.
                </div>
                <div className="pl-mono">
                  <div><span>product</span><b>not created</b></div>
                  <div><span>price · monthly</span><b>not created</b></div>
                  <div><span>price · annual</span><b>not created</b></div>
                </div>
              </div>
            )}
          </section>

          {!isNew && (
            <section className="fs-card st-card" aria-labelledby="pl-ed-accounts">
              <div className="st-card-head"><h2 id="pl-ed-accounts">Accounts on this plan</h2></div>
              <div className="pl-rail-body">
                {accounts.length === 0 ? (
                  <div className="st-faint">Nobody yet.</div>
                ) : (
                  <>
                    {accounts.map((a) => (
                      <div key={a.id} className="pl-ed-acct">
                        <div className="pl-ed-acct-text">
                          <Link to={`/platform/accounts/${a.id}?tab=billing`}>{a.name}</Link>
                          {a.billing_interval && plan && (
                            <div className="pl-ed-acct-sub">
                              {a.billing_interval === 'year'
                                ? `annual · ${money(plan.price_annual_cents, plan.currency)}`
                                : `monthly · ${money(plan.price_monthly_cents, plan.currency)}`}
                            </div>
                          )}
                        </div>
                        {a.subscription_status && (
                          <span className={`fs-pill ${a.subscription_status === 'trialing' ? 'warn'
                            : a.subscription_status === 'past_due' ? 'caution'
                              : a.subscription_status === 'active' ? 'ok' : 'neutral'}`}>
                            {a.subscription_status === 'trialing' ? 'trial'
                              : a.subscription_status === 'active' ? 'paid' : a.subscription_status}
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="pl-rail-foot">
                      Accounts on a free period are counted here but contribute no MRR until they convert.
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
      {guard.node}
    </form>
  );
}

/** One value in the monospace ids box, or a spinner while a push is minting it. */
function railValue(value: string | null | undefined, pushing: boolean) {
  if (pushing) return <b><span className="pl-spin" /></b>;
  return <b>{value || 'not created'}</b>;
}

/** One row of the rail's four-dot check list. */
function RailCheck({ status, title, detail }: {
  status: 'pass' | 'warn' | 'fail' | 'pending'; title: string; detail: string;
}) {
  return (
    <div className="sso-check">
      <span className={`sso-dot${status === 'pending' ? '' : ` ${status}`}`} />
      <div>
        <div className="sso-check-title">{title}</div>
        <div className={`sso-check-detail${status === 'warn' ? ' pl-warn-text' : ''}`}>{detail}</div>
      </div>
    </div>
  );
}

/** One of the three answers to "what happens to existing subscribers?". */
function MigrationCard({ value, current, onPick, title, detail, disabled }: {
  value: PriceMigration; current: PriceMigration; onPick: (m: PriceMigration) => void;
  title: string; detail: string; disabled?: boolean;
}) {
  return (
    <label className={`fc-radio${current === value ? ' on' : ''}`}>
      <input type="radio" name="pl-migration" checked={current === value} disabled={disabled}
        onChange={() => onPick(value)} />
      <span>
        <b>{title}</b>
        <small>{detail}</small>
      </span>
    </label>
  );
}

/**
 * One ceiling, or none at all — a row of the Limits card.
 *
 * The ∞ box is the whole point: it stores null rather than a big number, so
 * "unlimited" is a different kind of value and not a number to compare against.
 */
function LimitField({ limitKey, value, disabled, onChange }: {
  limitKey: keyof PlanLimits;
  value: number | null;
  disabled: boolean;
  onChange: (v: number | null) => void;
}) {
  const isStorage = limitKey === 'storageBytes';
  const shown = value === null ? '' : String(isStorage ? gbFromBytes(value) : value);
  // The field keeps its own text so it can be emptied mid-edit. A controlled
  // input that refuses "" cannot be backspaced through — the model only moves
  // when what has been typed is a number.
  const [text, setText] = useState(shown);
  useEffect(() => { setText(shown); }, [shown]);
  const id = `pl-ed-limit-${limitKey}`;

  return (
    <RowCard label={<label htmlFor={id}>{LIMIT_LABEL[limitKey]}{isStorage ? ' (GB)' : ''}</label>}>
      <div className="pl-ed-inline">
        <input id={id} className="fs-input pl-ed-num" value={text} disabled={disabled || value === null} inputMode="decimal"
          onChange={(e) => {
            setText(e.target.value);
            const n = Number(e.target.value);
            if (e.target.value === '' || Number.isNaN(n) || n < 0) return;
            onChange(isStorage ? bytesFromGb(n) : Math.round(n));
          }} />
        <label className="pl-ed-infinity">
          <input type="checkbox" checked={value === null} disabled={disabled}
            onChange={(e) => onChange(e.target.checked ? null : (isStorage ? 1000000000 : 1))} /> {INFINITY}
        </label>
        {isStorage && value !== null && <span className="pl-ed-hint">{formatStorage(value)}</span>}
      </div>
    </RowCard>
  );
}
